// NTFSDataSource.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  SET_NTFS_DIRECTORIES,
  SET_NTFS_PROPOSED,
} from "../../../../redux/constants/constants";
import HostField from "./HostField";
import DirectoriesAutocomplete from "./DirectoriesAutocomplete";
import ProcessAutocomplete from "./ProcessAutocomplete";
import NTFSButtons from "./NTFSButtons";
import {
  DOWNLOAD_NTFS_AGENT,
  DOWNLOAD_NTFS_CONFIGURATION,
  NTFS,
  ntfsIconMap,
  SET_EXCLUDE_PATTERNS,
  SET_NTFS_ICON_MAP,
  SET_SCANNED_PATHS,
} from "../dataSourceConstants";
import {
  FormControlLabel,
  makeStyles,
  Chip,
  Checkbox,
} from "@material-ui/core";
import InfoIcon from "../../../../assets/icons/InfoIcon.svg";
import { renderDataSourceIcons } from "../DatasourceIcons";
import NTFSPathsAutocomplete from "./NTFSPathsAutocomplete";
import NTFSScanTimeRange from "./NTFSScanTimeRange";
import {
  ExcludePatternsDescription,
  ExcludePatternsExample,
  ScanPathsDescription,
  ScanPathsExample,
} from "../../NTFS/ntfsConstants";
import NTFSCPULimit from "./NTFSCPULimit";
import NTFSMaxFileSize from "./NTFSMaxFileSize";

const useStyles = makeStyles(() => ({
  customErrorIndication: { color: "red" },
  customPadding: { padding: "10px 0px" },
  customDirectories: { padding: "10px 0px", marginBottom: "10px" },
  cancelButtonCustomStyles: {
    width: "124px",
    height: "32px",
    boxShadow: "0 5px 8px -3px rgb(1 18 121 / 50%)",
  },
  saveButtonCustomStyles: {
    marginLeft: "10px",
    textTransform: "none",
    width: "124px",
    height: "32px",
  },
  downloadButtonCustomStyles: {
    marginLeft: "5px",
    textTransform: "none",
    height: "32px",
    whiteSpace: "nowrap",
  },
  tooltipContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  tooltipHeading: {
    fontWeight: "bold",
    fontSize: "15px",
  },
  tooltipExample: {
    fontStyle: "italic",
    color: "gray",
    fontSize: "13px",
  },
}));

function NTFSDataSource({
  isEditMode,
  error,
  engine,
  removeErrorByKey,
  dataSourceData,
  toggleDrawer,
  saveDataSourceFieldData,
  staticInstructionsOpen,
  handleSaveActions,
  setNTFSType,
}) {
  const [ntfsConfigurationCheck, setNtfsConfigurationCheck] = useState(false);
  const classes = useStyles();
  const dispatch = useDispatch();
  const ntfsHost = useSelector((state) => state.CreateSourceReducer.ntfsHost);
  const ntfsDirectories = useSelector(
    (state) => state.CreateSourceReducer.ntfsDirectories,
  );
  const ntfsProcessNames = useSelector(
    (state) => state.CreateSourceReducer.ntfsProcessNames,
  );
  const ntfsScannedPaths = useSelector(
    (state) => state.CreateSourceReducer.scannedPaths || [],
  );

  const ntfsExcludePatterns = useSelector(
    (state) => state.CreateSourceReducer.excludePatterns || [],
  );
  const ntfsCPUCapacity = useSelector(
    (state) => state.CreateSourceReducer.maxCPUCapacity,
  );
  const ntfsStartScanTime = useSelector(
    (state) => state.CreateSourceReducer.startScanTime,
  );
  const ntfsEndScanTime = useSelector(
    (state) => state.CreateSourceReducer.endScanTime,
  );
  const ntfsMaxFileSize = useSelector(
    (state) => state.CreateSourceReducer.maxFileSize,
  );

  const iconMap = useSelector(
    (state) => state.CreateSourceReducer.iconMap,
  );
 

  useEffect(() => {
    if (!dataSourceData || !isEditMode) return;

    const isLocal =
      dataSourceData.hostType === "NTFS_LOCAL_ENDPOINT" ||
      dataSourceData.hostType?.code === "NTFS_LOCAL_ENDPOINT";

    const updatedIconMap = {
      NTFS_SERVER: {
        ...iconMap.NTFS_SERVER,
        selected: !isLocal,
      },
      NTFS_LOCAL: {
        ...iconMap.NTFS_LOCAL,
        selected: isLocal,
      },
    };

    dispatch({type: SET_NTFS_ICON_MAP, payload: updatedIconMap});
    setNTFSType(isLocal ? "NTFS_LOCAL_ENDPOINT" : "NTFS_SERVER");
  }, [dataSourceData, isEditMode, iconMap]);

  useEffect(() => {
    if (!iconMap || !iconMap.NTFS_SERVER) return;

    setNTFSType(
      iconMap.NTFS_SERVER.selected ? "NTFS_SERVER" : "NTFS_LOCAL_ENDPOINT",
    );
  }, [iconMap, setNTFSType]);

  const handleConfirmConfiguration = (event) =>
    setNtfsConfigurationCheck(event.target.checked);

  const handleNTFSConfig = () => {
    saveDataSourceFieldData();
    staticInstructionsOpen(true);
  };

  const handleDownloadActions = (event) => {
    dispatch({
      type: DOWNLOAD_NTFS_CONFIGURATION,
      payload: {
        id: dataSourceData.id,
        name: dataSourceData.aliasName,
        loc: dataSourceData?.hostType?.code,
      },
    });
  };

  const handleAgentDownloadActions = (event) => {
    dispatch({
      type: DOWNLOAD_NTFS_AGENT
    })
  };

  const handleAutocompleteTags = (value, getTagProps, loc) => {
    const getExistingValues = () => {
      switch (loc) {
        case "Directories":
          return dataSourceData.directoryNames || [];

        case "processNames":
          return dataSourceData.processNames || [];

        case "Scan Paths":
          return dataSourceData.scanPaths || [];

        case "Exclude Patterns":
          return dataSourceData.excludePatterns || [];

        default:
          return [];
      }
    };

    const existingValues = getExistingValues();

    return (
      <div className="chipContainer">
        {value.map((option, index) => {
          const isLocked = isEditMode && existingValues.includes(option);

          return (
            <Chip
              {...getTagProps({ index })}
              key={index}
              label={option}
              disabled={isLocked}
              onDelete={isLocked ? undefined : getTagProps({ index }).onDelete}
            />
          );
        })}
      </div>
    );
  };

  const isLocal = iconMap && iconMap.NTFS_LOCAL && iconMap.NTFS_LOCAL.selected;

  const handleSelectionChange = (altText, isIconClick) => {
    if (isEditMode) return;
    const updatedIconMap = {};
    for (const key in iconMap) {
      updatedIconMap[key] = {
        ...iconMap[key],
        selected: key === altText && isIconClick,
      };
    }
    dispatch({type: SET_NTFS_ICON_MAP, payload: updatedIconMap});
    removeErrorByKey("ntfsHost");
  };

  const handleAddNewDirectory = (event) => {
    if (
      (event.type === "keydown" && event.key === "Enter") ||
      event.type === "blur"
    ) {
      const newValue = event.target.value.trim();
      if (newValue && ntfsDirectories.indexOf(newValue) === -1) {
        dispatch({
          type: SET_NTFS_DIRECTORIES,
          payload: [...ntfsDirectories, newValue],
        });
        removeErrorByKey("ntfsDirectories");
      }
      event.target.value = "";
    }
  };

  const createAddNewHandler =
    ({ currentValues, dispatch, actionType, errorKey, removeErrorByKey }) =>
    (event) => {
      const isSubmit =
        (event.type === "keydown" && event.key === "Enter") ||
        event.type === "blur";

      if (!isSubmit) return;

      const newValue = event.target.value.trim();

      if (newValue && !currentValues.includes(newValue)) {
        dispatch({
          type: actionType,
          payload: [...currentValues, newValue],
        });
        removeErrorByKey(errorKey);
      }

      event.target.value = "";
    };

  const handleAddNewScannedPath = createAddNewHandler({
    currentValues: ntfsScannedPaths,
    dispatch,
    actionType: SET_SCANNED_PATHS,
    errorKey: "ntfsScannedPaths",
    removeErrorByKey,
  });

  const handleAddNewExcludePattern = createAddNewHandler({
    currentValues: ntfsExcludePatterns,
    dispatch,
    actionType: SET_EXCLUDE_PATTERNS,
    errorKey: "ntfsExcludePatterns",
    removeErrorByKey,
  });

  const handleAddNewProposed = (event) => {
    if (
      (event.type === "keydown" && event.key === "Enter") ||
      event.type === "blur"
    ) {
      const newValue = event.target.value.trim();
      if (newValue) {
        dispatch({
          type: SET_NTFS_PROPOSED,
          payload: [...ntfsProcessNames, newValue],
        });
        removeErrorByKey("ntfsProcessNames");
      }
      event.target.value = "";
    }
  };

  const renderNTFSConsent = () => (
    <div
      className={
        ntfsConfigurationCheck
          ? `logConfigurationDoneContainer logSourceConfigDone`
          : `logConfigurationDoneContainer ${isEditMode ? "editMode" : ""}`
      }
    >
      {!isEditMode && (
        <FormControlLabel
          className="confirmContainer"
          control={
            <Checkbox
              color="primary"
              checked={ntfsConfigurationCheck}
              onChange={handleConfirmConfiguration}
              disabled={isEditMode}
            />
          }
          label={`Please accept if NTFS ${isLocal ? "local" : "server" } configuration is done`}
          labelPlacement="end"
          style={{ fontSize: "14px", marginRight: "5px" }}
        />
      )}
      <div className="WinlogbeatContainer">
        <img src={InfoIcon} alt="info icon" />
        <div className="winlogbeatText" onClick={handleNTFSConfig}>
          Link To NTFS configuration
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <>
        {renderDataSourceIcons(
          isEditMode,
          iconMap,
          handleSelectionChange,
          engine,
          dataSourceData,
        )}
      </>
      <HostField
        ntfsHost={ntfsHost}
        classes={classes}
        dispatch={dispatch}
        removeErrorByKey={removeErrorByKey}
        isEditMode={isEditMode}
        error={error}
        type={iconMap}
      />
      {iconMap && iconMap.NTFS_SERVER && iconMap.NTFS_SERVER.selected && (
        <>
          <DirectoriesAutocomplete
            ntfsDirectories={ntfsDirectories}
            classes={classes}
            dataSourceData={dataSourceData}
            dispatch={dispatch}
            removeErrorByKey={removeErrorByKey}
            error={error}
            handleAddNewDirectory={handleAddNewDirectory}
            handleAutocompleteTags={handleAutocompleteTags}
          />
          <ProcessAutocomplete
            ntfsProcessNames={ntfsProcessNames}
            classes={classes}
            dataSourceData={dataSourceData}
            dispatch={dispatch}
            removeErrorByKey={removeErrorByKey}
            error={error}
            handleAddNewProposed={handleAddNewProposed}
            handleAutocompleteTags={handleAutocompleteTags}
          />
        </>
      )}
      {isLocal && (
        <>
          <NTFSPathsAutocomplete
            title="Scan Paths"
            placeholder="Scanned Paths"
            value={ntfsScannedPaths}
            actionType={SET_SCANNED_PATHS}
            existingValues={dataSourceData?.scanPaths || []}
            errorKey="ntfsScannedPaths"
            error={error}
            classes={classes}
            dispatch={dispatch}
            removeErrorByKey={removeErrorByKey}
            handleAddNew={handleAddNewScannedPath}
            handleAutocompleteTags={handleAutocompleteTags}
            description={ScanPathsDescription}
            example={ScanPathsExample}
            isEditMode={isEditMode}
          />
          <NTFSPathsAutocomplete
            title="Exclude Patterns"
            placeholder="Exclude Patterns"
            value={ntfsExcludePatterns}
            actionType={SET_EXCLUDE_PATTERNS}
            existingValues={dataSourceData.excludePatterns || []}
            errorKey="ntfsExcludePatterns"
            error={error}
            classes={classes}
            dispatch={dispatch}
            removeErrorByKey={removeErrorByKey}
            handleAddNew={handleAddNewExcludePattern}
            handleAutocompleteTags={handleAutocompleteTags}
            description={ExcludePatternsDescription}
            example={ExcludePatternsExample}
            isEditMode={isEditMode}
          />
          <NTFSScanTimeRange
            startTime={ntfsStartScanTime}
            endTime={ntfsEndScanTime}
            dispatch={dispatch}
            removeErrorByKey={removeErrorByKey}
            error={error}
            classes={classes}
          />
          <NTFSCPULimit
            ntfsCpuLimit={ntfsCPUCapacity}
            classes={classes}
            dispatch={dispatch}
            removeErrorByKey={removeErrorByKey}
            isEditMode={isEditMode}
            error={error}
          />
          <NTFSMaxFileSize
            ntfsMaxFileSize={ntfsMaxFileSize}
            classes={classes}
            dispatch={dispatch}
            removeErrorByKey={removeErrorByKey}
            isEditMode={isEditMode}
            error={error}
          />
        </>
      )}
      {renderNTFSConsent()}
      <NTFSButtons
        classes={classes}
        iconMap={iconMap}
        toggleDrawer={toggleDrawer}
        handleSaveActions={handleSaveActions}
        handleDownloadActions={handleDownloadActions}
        isEditMode={isEditMode}
        ntfsConfigurationCheck={ntfsConfigurationCheck}
        handleAgentDownloadActions={handleAgentDownloadActions}
      />
    </div>
  );
}

export default NTFSDataSource;
